import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Container, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import {
  FaHeart,
  FaMoneyBillWave,
  FaRegSmile,
  FaShieldAlt,
  FaStar,
} from "react-icons/fa";
import { FaHandsClapping } from "react-icons/fa6";
import { useLocation, useParams } from "react-router-dom";
import {
  notifyBugsnagError,
  removeDashed,
  showToast,
} from "../../../../../common/utils/Functions";
import DashboardLayout from "../../../../../components/layouts/DashboardLayout";
import ReactLoader from "../../../../../components/reactLoader/ReactLoader";
import useFetch from "../../../../../hooks/useFetch";
import PageNotFound from "../../../../PageNotFound/PageNotFound";
import noimage from "../../../../../assests/images/no-image.png";
import toptipperLogo from "../../../../../assests/Logo.svg";
import styles from "./scoreboard.module.scss";

const CircleDollarSignIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
    <path d="M12 18V6" />
  </svg>
);

const WalletIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
    <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
  </svg>
);

const UsersRoundIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 21a8 8 0 0 0-16 0" />
    <circle cx="10" cy="8" r="5" />
    <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
  </svg>
);

const TrophyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10 14.66V17a1 1 0 0 1-1 1 2 2 0 0 0-2 2v2" />
    <path d="M14 14.66V17a1 1 0 0 0 1 1 2 2 0 0 1 2 2v2" />
    <path d="M17.916 10H19.5A2.5 2.5 0 0 0 22 7.5V5a1 1 0 0 0-1-1h-3" />
    <path d="M4 22h16" />
    <path d="M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z" />
    <path d="M6.084 10H4.5A2.5 2.5 0 0 1 2 7.5V5a1 1 0 0 1 1-1h3" />
  </svg>
);

const MessageCircleMoreIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
    <path d="M8 12h.01" />
    <path d="M12 12h.01" />
    <path d="M16 12h.01" />
  </svg>
);

const NOTE_SENTIMENT_META = [
  { icon: <FaHeart />, className: styles.sentimentHeart },
  { icon: <FaRegSmile />, className: styles.sentimentSmile },
  { icon: <FaHandsClapping />, className: styles.sentimentClap },
  { icon: <FaStar />, className: styles.sentimentStar },
  { icon: <FaHeart />, className: styles.sentimentDefault },
];

const NOTE_TOOLTIP_LENGTH = 120;

const CustomerNoteText = ({
  note,
  noteId,
}: {
  note: string;
  noteId: string;
}) => {
  const trimmedNote = note?.trim() ?? "";

  if (!trimmedNote) {
    return (
      <div className={`${styles.noteText} ${styles.noteTextEmpty}`}>--</div>
    );
  }

  // Full note is always in the DOM so mobile can wrap it; desktop truncates via CSS.
  const showTooltip = trimmedNote.length > NOTE_TOOLTIP_LENGTH;

  const noteContent = (
    <div
      className={`${styles.noteText} ${showTooltip ? styles.noteTextTruncated : ""}`}
    >
      {trimmedNote}
    </div>
  );

  if (!showTooltip) {
    return noteContent;
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`customer-note-${noteId}`} style={{ maxWidth: 320 }}>
          {trimmedNote}
        </Tooltip>
      }
    >
      {noteContent}
    </OverlayTrigger>
  );
};

const formatCurrency = (value?: number | null) =>
  value || value === 0
    ? value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      })
    : removeDashed;

// const getRelativeTimeLabel = (value?: string) => {
//   if (!value) {
//     return removeDashed;
//   }

//   const date = moment(value);
//   if (date.isSame(moment(), "day")) {
//     return "Today";
//   }
//   if (date.isSame(moment().subtract(1, "day"), "day")) {
//     return "Yesterday";
//   }
//   return date.fromNow();
// };
const getRelativeTimeLabel = (value?: string) => {
  if (!value) {
    return removeDashed;
  }

  const date = moment(value);
  if (!date.isValid()) {
    return removeDashed;
  }

  const now = moment();
  const diffSeconds = now.diff(date, "seconds");
  const diffMinutes = now.diff(date, "minutes");
  const diffHours = now.diff(date, "hours");
  const diffDays = now.diff(date, "days");

  const pluralize = (count: number, unit: string) =>
    `${count} ${unit}${count === 1 ? "" : "s"} ago`;

  if (diffSeconds < 60) {
    return "Just now";
  }
  if (diffMinutes < 60) {
    return pluralize(diffMinutes, "minute");
  }
  if (date.isSame(now, "day")) {
    return pluralize(diffHours, "hour");
  }
  if (date.isSame(now.clone().subtract(1, "day"), "day")) {
    return "Yesterday";
  }
  if (diffDays < 7) {
    return pluralize(diffDays, "day");
  }

  const diffMonths = now.diff(date, "months");
  if (diffMonths < 1) {
    return pluralize(now.diff(date, "weeks"), "week");
  }
  if (diffMonths < 12) {
    return pluralize(diffMonths, "month");
  }

  return pluralize(now.diff(date, "years"), "year");
};

type CustomerNoteApiItem = {
  id?: number | string;
  uuid?: string;
  note?: string | null;
  net_tip_amount?: number | null;
  date?: string;
};

type NoteViewItem = {
  id: string;
  note: string;
  time: string;
  amount: string;
  icon: JSX.Element;
  className: string;
};

const mapNoteItem = (item: CustomerNoteApiItem, index: number): NoteViewItem => {
  const sentiment = NOTE_SENTIMENT_META[index % NOTE_SENTIMENT_META.length];
  return {
    id: `${item?.uuid || item?.id || item?.date || "note"}-${index}`,
    note: item?.note?.trim() || removeDashed,
    time: getRelativeTimeLabel(item?.date),
    amount: formatCurrency(item?.net_tip_amount),
    icon: sentiment.icon,
    className: sentiment.className,
  };
};

const CalendarStarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="m16.365,14.183c0,.379-.264.698-.566.866l-1.564.87.694,1.893c.134.367.013.778-.299,1.013h0c-.319.24-.759.237-1.075-.007l-1.556-1.203-1.556,1.203c-.316.244-.756.247-1.075.007h0c-.312-.235-.433-.646-.299-1.013l.694-1.893-1.564-.87c-.302-.168-.566-.487-.566-.866,0-.321.279-.676.731-.676h2.247l.596-2.283c.094-.362.419-.614.792-.621.373.007.698.259.792.621l.596,2.283h2.247c.452,0,.731.354.731.676Zm2.135-12.183h-.5v-.5c0-.829-.671-1.5-1.5-1.5s-1.5.671-1.5,1.5v.5h-6v-.5c0-.829-.671-1.5-1.5-1.5s-1.5.671-1.5,1.5v.5h-.5C2.467,2,0,4.467,0,7.5v11c0,3.033,2.467,5.5,5.5,5.5h13c3.033,0,5.5-2.467,5.5-5.5V7.5c0-3.033-2.467-5.5-5.5-5.5Zm0,19H5.5c-1.378,0-2.5-1.122-2.5-2.5v-9.5h18v9.5c0,1.378-1.122,2.5-2.5,2.5Z" />
  </svg>
);

const ShieldStarIcon = () => (

  <span className={styles.compositeIcon} aria-hidden="true">
  <FaShieldAlt />

</span>
);

const StarRating = ({ score }: { score: number }) => {
  const clamped = Math.min(5, Math.max(0, Number(score) || 0));
  const fullStars = Math.floor(clamped);
  const fraction = clamped - fullStars;
  const hasHalfStar = fraction >= 0.25 && fraction < 0.75;
  const roundedUp = fraction >= 0.75 ? 1 : 0;
  const filledCount = Math.min(5, fullStars + roundedUp);

  return (
    <div
      className={styles.starRating}
      aria-label={`${clamped.toFixed(1)} out of 5`}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        if (star <= filledCount) {
          return <FaStar key={star} className={styles.starFilled} />;
        }
        if (star === filledCount + 1 && hasHalfStar) {
          return (
            <span key={star} className={styles.halfStar}>
              <FaStar className={styles.starEmpty} />
              <FaStar className={styles.starHalfFill} />
            </span>
          );
        }
        return <FaStar key={star} className={styles.starEmpty} />;
      })}
    </div>
  );
};

const Scoreboard = () => {
  const { state } = useLocation();
  const location = useLocation();
  const { id: routePropertyUuid } = useParams();
  const propertyUuid: string | undefined =
    state?.uuid || routePropertyUuid || undefined;
  const isPublicPage =
    location.pathname.includes("/public/") &&
    !location.pathname.includes("manage-properties");
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [allNotes, setAllNotes] = useState<NoteViewItem[]>([]);

  const [
    getScoreboardData,
    {
      response: scoreboardResponse,
      error: scoreboardError,
      loading: scoreboardLoading,
    },
  ] = useFetch("/dashboard/scoreboard-report", {
    method: "POST",
  });

  const [
    getAllNotesData,
    {
      response: allNotesResponse,
      error: allNotesError,
      loading: allNotesLoading,
    },
  ] = useFetch("/dashboard/scoreboard-report", {
    method: "POST",
  });

  useEffect(() => {
    if (!propertyUuid) {
      return;
    }

    try {
      getScoreboardData({
        uuid: propertyUuid,
        start: 0,
        limit: 5,
      });
    } catch (e: any) {
      notifyBugsnagError(e, {
        api: "getScoreboardData",
      });
    }
  }, [propertyUuid]);

  useEffect(() => {
    if (allNotesResponse) {
      const list: CustomerNoteApiItem[] =
        allNotesResponse.data?.recent_customer_notes?.list || [];
      setAllNotes(list.map(mapNoteItem));
      setShowAllNotes(true);
    }
  }, [allNotesResponse]);

  useEffect(() => {
    if (allNotesError) {
      showToast(allNotesError.message as string, "error");
    }
  }, [allNotesError]);

  const handleViewAllNotes = () => {
    try {
      getAllNotesData({
        uuid: propertyUuid,
        start: 0,
        limit: -1,
      });
    } catch (e: any) {
      notifyBugsnagError(e, {
        api: "getAllNotesData",
      });
    }
  };

  const scoreboardErrorCode = Number(
    (scoreboardError as { code?: number | string } | undefined)?.code
  );
  const isNotFound = scoreboardErrorCode === 404;

  useEffect(() => {
    if (scoreboardError && !isNotFound) {
      showToast(scoreboardError?.message as string, "error");
    }
  }, [scoreboardError, isNotFound]);

  if (isNotFound) {
    return <PageNotFound />;
  }

  const scoreboardData = scoreboardResponse?.data;
  const propertyName = scoreboardData?.property_name || "";
  const propertyLogo = scoreboardData?.property_logo || "";
  const showServiceScore = Number(scoreboardData?.is_service_score) === 1;
  const today = scoreboardData?.today;
  const lifetime = scoreboardData?.lifetime;
  const percentChange =
    typeof today?.net_tips_change_percent === "number"
      ? today.net_tips_change_percent
      : null;

  const todayStats = [
    {
      title: "Tips Today",
      value: today?.tips_count ?? removeDashed,
      subtitle: "Tip transactions today",
      icon: <CircleDollarSignIcon />,
      key: "tipsToday",
      theme: "blue",
    },
    showServiceScore
      ? {
          title: "Today's Service Score",
          value:
            typeof today?.service_score === "number"
              ? today.service_score
              : null,
          subtitle: "Customer satisfaction today",
          icon: <CalendarStarIcon />,
          key: "todayThirdMetric",
          theme: "purple",
          isStarRating: true,
        }
      : {
          title: "Today's Average Tip Amount",
          value: formatCurrency(today?.average_tip_amount),
          subtitle: "Average tip today",
          icon: <UsersRoundIcon />,
          key: "todayThirdMetric",
          theme: "purple",
        },
    {
      title: "Today's Net Tips",
      value: formatCurrency(today?.net_tips),
      subtitle: "After fees",
      icon: <WalletIcon />,
      key: "todayNetTips",
      showTrend: true,
      theme: "green",
    },
  ];

  const lifetimeStats = [
    {
      title: "Total Tips Received",
      value: lifetime?.total_tips ?? removeDashed,
      subtitle: "All time tip transactions",
      icon: <TrophyIcon />,
      key: "totalTips",
      theme: "orange",
    },
    showServiceScore
      ? {
          title: "Lifetime Service Score",
          value:
            typeof lifetime?.service_score === "number"
              ? lifetime.service_score
              : null,
          subtitle: "Overall customer satisfaction",
          icon: <ShieldStarIcon />,
          key: "lifetimeThirdMetric",
          theme: "mint",
          isStarRating: true,
        }
      : {
          title: "Lifetime Average Tip Amount",
          value: formatCurrency(lifetime?.average_tip_amount),
          subtitle: "Lifetime average tip",
          icon: <UsersRoundIcon />,
          key: "lifetimeThirdMetric",
          theme: "mint",
        },
    {
      title: "Lifetime Net Tips",
      value: formatCurrency(lifetime?.net_tips),
      subtitle: "After fees",
      icon: <FaMoneyBillWave />,
      key: "lifetimeNetTips",
      theme: "teal",
    },
  ];

  const notesList: NoteViewItem[] = (
    scoreboardData?.recent_customer_notes?.list || []
  ).map(mapNoteItem);

  const visibleNotes = notesList.slice(0, 5);
  const notesToDisplay = showAllNotes ? allNotes : visibleNotes;
  const totalNotesCount = Number(
    scoreboardData?.recent_customer_notes?.totalRecords ?? 0
  );
  const canViewAllNotes = totalNotesCount > 5;

  const isLoading = scoreboardLoading && !scoreboardResponse;

  const themeClassMap: Record<string, string> = {
    blue: styles.themeBlue,
    green: styles.themeGreen,
    purple: styles.themePurple,
    orange: styles.themeOrange,
    teal: styles.themeTeal,
    mint: styles.themeMint,
    pink: styles.themePink,
  };

  const mobileOrderClassMap: Record<number, string> = {
    1: styles.mobileOrder1,
    2: styles.mobileOrder2,
    3: styles.mobileOrder3,
    4: styles.mobileOrder4,
    5: styles.mobileOrder5,
    6: styles.mobileOrder6,
  };

  type StatCard = {
    title: string;
    value: string | number | null;
    subtitle: string;
    icon: JSX.Element;
    key: string;
    showTrend?: boolean;
    theme: string;
    isStarRating?: boolean;
    mobileOrder?: number;
  };

  const renderStatCard = (item: StatCard) => (
    <div
      className={`${styles.card_outer} ${
        item.mobileOrder ? mobileOrderClassMap[item.mobileOrder] || "" : ""
      }`}
      key={item.key}
    >
      <div className={`${styles.card} ${themeClassMap[item.theme] || ""}`}>
        <div className={styles.icon}>{item.icon}</div>
        <div className={styles.content}>
          <div className={styles.title}>{item.title}</div>
          {item.isStarRating ? (
            typeof item.value === "number" ? (
              <StarRating score={item.value} />
            ) : (
              <div className={styles.amount}>{removeDashed}</div>
            )
          ) : (
            <div className={styles.amount}>{item.value}</div>
          )}
          <div className={styles.subtitle}>{item.subtitle}</div>
          {item.showTrend && percentChange !== null ? (
            <div className={styles.trendRow}>
              <span
                className={
                  percentChange > 0
                    ? styles.trendUp
                    : percentChange < 0
                      ? styles.trendDown
                      : styles.trendFlat
                }
              >
                {percentChange > 0
                  ? "↑"
                  : percentChange < 0
                    ? "↓"
                    : removeDashed}{" "}
                {Math.abs(percentChange)}%
              </span>
              <span className={styles.trendLabel}>vs yesterday</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  // Tablet/mobile 2-col: Today (left) paired with Lifetime (right) by metric type.
  const todayStatsWithOrder: StatCard[] = todayStats.map((item, index) => ({
    ...item,
    mobileOrder: index * 2 + 1,
  }));
  const lifetimeStatsWithOrder: StatCard[] = lifetimeStats.map((item, index) => ({
    ...item,
    mobileOrder: index * 2 + 2,
  }));

  const pageContent = (
    <div className={styles.dash_page}>
      <Container>
        <Row className="g-xl-4 g-3">
          <Col xxl={12} xl={12}>
            <div className={styles.headingWrap}>
              {(propertyLogo || propertyName) && (
                <div className={styles.propertyBrand}>
                  <div className={styles.propertyLogo}>
                    <img
                      src={propertyLogo || noimage}
                      alt={propertyName || "Property logo"}
                    />
                  </div>
                  {propertyName ? (
                    <span className={styles.propertyName}>{propertyName}</span>
                  ) : null}
                </div>
              )}
              <h2>Team Tips Dashboard</h2>
              <div className={styles.poweredBy}>
                <span>Powered by</span>
                <img src={toptipperLogo} alt="TopTipper" />
              </div>
            </div>
          </Col>
        </Row>

        {isLoading ? (
          <ReactLoader />
        ) : (
          <>
            <div className={styles.cardsWrap}>
              <div className={`${styles.sectionLabel} ${styles.sectionLabelFull}`}>
                Today at a glance
              </div>
              {todayStatsWithOrder.map(renderStatCard)}

              <div className={`${styles.sectionLabel} ${styles.sectionLabelFull}`}>
                All time
              </div>
              {lifetimeStatsWithOrder.map(renderStatCard)}
            </div>

            <section className={styles.notesPanel}>
              <div className={styles.notesHeader}>
                <div className={styles.notesTitle}>
                  <span className={styles.chatIcon}>
                    <MessageCircleMoreIcon />
                  </span>
                  <h3>Recent Customer Notes</h3>
                </div>
                {canViewAllNotes && !showAllNotes && (
                  <button
                    type="button"
                    className={`${styles.viewAll} ${styles.viewAllDesktop}`}
                    onClick={handleViewAllNotes}
                    disabled={allNotesLoading}
                  >
                    {allNotesLoading ? "Loading..." : "View all notes →"}
                  </button>
                )}
              </div>

              <div>
                {notesToDisplay.length ? (
                  notesToDisplay.map((note) => (
                    <div key={note.id} className={styles.noteRow}>
                      <span
                        className={`${styles.sentimentIcon} ${note.className}`}
                      >
                        {note.icon}
                      </span>
                      <div className={styles.noteMain}>
                        <CustomerNoteText note={note.note} noteId={note.id} />
                      </div>
                      <span className={styles.noteTime}>{note.time}</span>
                      <span className={styles.noteAmount}>
                        {note.amount ? `${note.amount} tip` : "--"}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    No customer notes yet.
                  </div>
                )}
              </div>

              {canViewAllNotes && !showAllNotes && (
                <button
                  type="button"
                  className={`${styles.viewAll} ${styles.viewAllMobile}`}
                  onClick={handleViewAllNotes}
                  disabled={allNotesLoading}
                >
                  {allNotesLoading ? "Loading..." : "View all notes →"}
                </button>
              )}
            </section>
          </>
        )}
      </Container>
    </div>
  );

  if (isPublicPage) {
    return <div className={styles.publicPage}>{pageContent}</div>;
  }

  return <DashboardLayout>{pageContent}</DashboardLayout>;
};

export default Scoreboard;
